// src/pages/TermsOfSale.jsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export const TermsOfSale = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      {/* Otsikko */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          mb: 4,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight={700} gutterBottom>
            📦 Käyttöehdot
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Pakettihuolto Oy | Päivitetty viimeksi: 16. tammikuuta 2026
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 4, md: 6 }, mb: 4 }}>

          {/* 1. Yleiset ehdot */}
          <section id="general">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              1. Yleiset ehdot
            </Typography>
            <Typography paragraph>
              Nämä myyntiehdot ("Ehdot") säätelevät alustamme kautta tarjottavien toimitus- ja logistiikkapalveluiden ostamista ja käyttöä ("me", "meidän" tai "meistä"). Tilauksen tekemällä sitoudut noudattamaan näitä Ehtoja ("Asiakas", "sinä" tai "sinun").
            </Typography>
            <Typography paragraph>
              Alustamme yhdistää asiakkaat erilaisiin toimituspalveluntarjoajiin tarjoten useita toimitusvaihtoehtoja eri tarpeisiin. Nämä Ehdot koskevat kaikkia verkkosivustomme, mobiilisovelluksemme tai muiden hallinnoimiemme alustojen kautta ostettuja palveluja.
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Yrityksen tiedot
              </Typography>
              <Typography variant="body2"><strong>Yrityksen nimi:</strong> Pakettihuolto Oy</Typography>
              <Typography variant="body2"><strong>Y-tunnus:</strong> 3219435-6</Typography>
              <Typography variant="body2"><strong>Osoite:</strong> Suomi</Typography>
              <Typography variant="body2"><strong>Sähköposti:</strong> info@pakettihuolto.fi</Typography>
              <Typography variant="body2"><strong>Puhelin:</strong> +358 50 505 8167</Typography>
              <Typography variant="body2"><strong>Asiakaspalveluaika:</strong> Maanantai–perjantai, 9:00–17:00 (EET/EEST)</Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 2. Palvelut */}
          <section id="services">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              2. Tarjottavat palvelut
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              2.1 Ympäristöystävälliset toimitusvaihtoehdot
            </Typography>
            <Typography paragraph>
              Kestävän kehityksen mukaiset toimitusratkaisut, jotka voivat hyödyntää julkisen liikenteen verkostoja ja optimoituja reittejä kustannustehokkaan ja ympäristötietoisen toimituksen toteuttamiseksi.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              2.2 Pikatoimitukset
            </Typography>
            <Typography paragraph>
              Nopea toimituspalvelu, jossa on prioriteettikäsittely ja taatut toimitusajat kiireellisille lähetyksille.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              2.3 Standarditoimitus
            </Typography>
            <Typography paragraph>
              Tavallinen toimituspalvelu, joka tarjoaa luotettavan toimituksen kilpailukykyiseen hintaan.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              2.4 Lisäpalvelut
            </Typography>
            <Typography paragraph>Voimme tarjota lisäpalveluja, kuten:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Kylmäkuljetus</Typography></li>
              <li><Typography>Palautuspakettien käsittely</Typography></li>
              <li><Typography>Joustavat nouto- ja toimitusvaihtoehdot</Typography></li>
              <li><Typography>Pakettien seuranta ja ilmoitukset</Typography></li>
            </Box>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 3. Tilaaminen ja sopimuksen syntyminen */}
          <section id="ordering">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              3. Tilaaminen ja sopimuksen syntyminen
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              3.1 Tilausprosessi
            </Typography>
            <Box component="ol" sx={{ ml: 4 }}>
              <li><Typography>Valitse toimituspalvelu ja syötä pakettitiedot</Typography></li>
              <li><Typography>Anna nouto- ja toimitusosoitteet</Typography></li>
              <li><Typography>Valitse toimitusnopeus ja lisäpalvelut</Typography></li>
              <li><Typography>Tarkista tilauksen yhteenveto ja hinta</Typography></li>
              <li><Typography>Suorita maksu tai valitse laskutusmaksu</Typography></li>
              <li><Typography>Vastaanota tilausvahvistus sähköpostitse</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              3.2 Sopimuksen syntyminen
            </Typography>
            <Typography paragraph>
              Sitova sopimus syntyy, kun saat tilausvahvistussähköpostin. Vahvistus sisältää tilausnumerosi, palvelun tiedot ja kokonaishinnan.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              3.3 Tietojen oikeellisuus
            </Typography>
            <Typography paragraph>
              Olet vastuussa siitä, että antamasi tiedot (osoitteet, yhteystiedot, pakettimäärittelyt) ovat oikein. Emme vastaa viivästyksistä tai toimitusepäonnistumisista, jotka johtuvat virheellisistä tiedoista.
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 4. Maksaminen */}
          <section id="payment">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              4. Maksaminen
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              4.1 Maksutavat
            </Typography>
            <Typography paragraph>Hyväksymme seuraavat maksutavat:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Luotto-/pankkikortit:</strong> Visa, Mastercard, American Express</Typography></li>
              <li><Typography><strong>Lasku:</strong> Saatavilla yritysasiakkaille ja varmistetuille tileille</Typography></li>
              <li><Typography><strong>Verkkopankkimaksu:</strong> Suomalaiset verkkopankkimaksut</Typography></li>
              <li><Typography><strong>Mobiilimaksu:</strong> MobilePay, Apple Pay</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              4.2 Maksuehdot
            </Typography>
            <Typography paragraph>
              <strong>Korttimaksut:</strong> Käsitellään välittömästi tilausvahvistuksen yhteydessä.
            </Typography>
            <Typography paragraph>
              <strong>Laskumaksut:</strong>
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Maksuaika: 14 päivää laskun päivämäärästä</Typography></li>
              <li><Typography>Viivästyskorko: 8 % vuodessa + muistutuskulut 5 €</Typography></li>
              <li><Typography>Maksamattomat laskut voidaan siirtää perintätoimiston käsiteltäväksi</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              4.3 Hinnoittelu
            </Typography>
            <Typography paragraph>
              Kaikki hinnat ilmoitetaan euroissa (€) ja sisältävät arvonlisäveron (24 %, ellei toisin mainita). Hinnat lasketaan seuraavien tekijöiden perusteella:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Paketin paino ja mitat</Typography></li>
              <li><Typography>Toimitusmatka</Typography></li>
              <li><Typography>Valittu toimitusnopeus</Typography></li>
              <li><Typography>Lisäpalvelut (kylmäkuljetus jne.)</Typography></li>
            </Box>

            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Hintatakuu: Kassalla näytetty hinta on lopullinen hinta. Vahvistuksen jälkeen ei lisätä piilomaksuja tai lisäkuluja.
              </Typography>
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              4.4 Yritysasiakkaat
            </Typography>
            <Typography paragraph>
              Yritysasiakkaat voivat hakea laskutusoikeutta toimittamalla:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Voimassa oleva suomalainen Y-tunnus</Typography></li>
              <li><Typography>Yrityksen rekisteröintidokumentit</Typography></li>
              <li><Typography>Nimenkirjoitusoikeuden omaavan henkilön tiedot</Typography></li>
            </Box>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 5. Peruuttamisoikeus */}
          <section id="withdrawal">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              5. Peruuttamisoikeus
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.1 Kuluttajan oikeudet
            </Typography>
            <Typography paragraph>
              Suomen kuluttajansuojalain mukaan kuluttajilla on oikeus peruuttaa tilauksensa 14 päivän kuluessa ilman erityistä syytä.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.2 Peruuttamisaika
            </Typography>
            <Typography paragraph>14 päivän peruuttamisaika alkaa:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Päivästä, jona vastaanotat tilausvahvistuksen, TAI</Typography></li>
              <li><Typography>Ennen sovittua noutoa, sen mukaan, kumpi tulee ensin</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.3 Peruuttamisoikeuden käyttäminen
            </Typography>
            <Typography paragraph>Peruuttaaksesi tilauksesi sinun tulee:</Typography>
            <Box component="ol" sx={{ ml: 4 }}>
              <li><Typography>Lähettää selkeä kirjallinen ilmoitus osoitteeseen: info@pakettihuolto.fi</Typography></li>
              <li><Typography>Ilmoittaa tilausnumero ja peruutuksen syy (valinnainen)</Typography></li>
              <li><Typography>Toimittaa ilmoitus ennen paketin noutoa</Typography></li>
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Tärkeää:</strong> Kun pakettisi on noudettu ja toimitus on alkanut, palvelun katsotaan olevan suoritettu eikä peruuttamisoikeutta enää sovelleta. Voit silti pyytää toimituksen peruuttamista, mutta peruutuskuluja saattaa aiheutua.
              </Typography>
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.4 Hyvitykset
            </Typography>
            <Typography paragraph>Jos käytät peruuttamisoikeuttasi asianmukaisesti:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Täysi hyvitys käsitellään 14 päivän kuluessa</Typography></li>
              <li><Typography>Hyvitys suoritetaan samalla maksutavalla kuin alkuperäinen maksu</Typography></li>
              <li><Typography>Vähennyksiä tai maksuja ei sovelleta</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.5 Peruuttaminen noudon jälkeen
            </Typography>
            <Typography paragraph>Jos pyydät peruuttamista noudon jälkeen:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Palautuskulut vähennetään hyvityksestä</Typography></li>
              <li><Typography>Käsittelymaksu 10 € voi aiheutua</Typography></li>
              <li><Typography>Osittainen hyvitys suorittamattoman palvelun osuudesta</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.6 Poikkeukset
            </Typography>
            <Typography paragraph>Peruuttamisoikeutta ei sovelleta:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Kokonaan suoritettuihin palveluihin suostumuksellasi</Typography></li>
              <li><Typography>Yritysten välisiin transaktioihin</Typography></li>
              <li><Typography>Räätälöityihin toimitusratkaisuihin, joissa on erityinen aikataulutus</Typography></li>
            </Box>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 6. Palautukset */}
          <section id="returns">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              6. Palautukset ja palautuspaketit
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              6.1 Palautuspakettien palvelu
            </Typography>
            <Typography paragraph>
              Palautuspakettien palvelumme avulla voit lähettää tuotteita takaisin jälleenmyyjille tai muille osapuolille:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Sama hinnoittelu kuin tavallisissa toimituksissa</Typography></li>
              <li><Typography>Palautusetikettejä voi luoda alustamme kautta</Typography></li>
              <li><Typography>Seuranta saatavilla kaikille palautuksille</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              6.2 Vaurioituneet tai kadonneet paketit
            </Typography>
            <Typography paragraph>Jos pakettisi vaurioituu tai katoaa kuljetuksen aikana:</Typography>
            <Box component="ol" sx={{ ml: 4 }}>
              <li><Typography>Ilmoita ongelmasta 7 päivän kuluessa arvioidusta toimituspäivästä</Typography></li>
              <li><Typography>Toimita tilausnumero ja valokuvat (vaurion tapauksessa)</Typography></li>
              <li><Typography>Tutkimme asian ja vastaamme 14 arkipäivän kuluessa</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              6.3 Vastuunrajoitukset
            </Typography>
            <Typography paragraph>Vastuumme kadonneista tai vaurioituneista paketeista on rajoitettu seuraavasti:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Standarditoimitus:</strong> 100 € per paketti</Typography></li>
              <li><Typography><strong>Pikatoimitus:</strong> 200 € per paketti</Typography></li>
              <li><Typography><strong>Ilmoitettu arvo:</strong> Enintään 1 000 € lisävakuutuksella (lisämaksu vaaditaan)</Typography></li>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Vakuutus:</strong> Lisävakuutus arvokkaille tuotteille on ostettavissa kassalla hintaan 2 € per ilmoitetun arvon 100 €.
              </Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 7. Reklamaatiot */}
          <section id="complaints">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              7. Reklamaatioiden käsittely
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              7.1 Reklamaation tekeminen
            </Typography>
            <Typography paragraph>Jos olet tyytymätön palveluumme, ota meihin yhteyttä:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Sähköposti:</strong> info@pakettihuolto.fi</Typography></li>
              <li><Typography><strong>Puhelin:</strong> +358 50 505 8167</Typography></li>
              <li><Typography><strong>Posti:</strong> Pakettihuolto Oy, Suomi</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              7.2 Tarvittavat tiedot
            </Typography>
            <Typography paragraph>Sisällytä reklamaatioosi:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Tilausnumero</Typography></li>
              <li><Typography>Palvelun päivämäärä</Typography></li>
              <li><Typography>Kuvaus ongelmasta</Typography></li>
              <li><Typography>Tukimateriaali (kuvat, sähköpostit jne.)</Typography></li>
              <li><Typography>Yhteystietosi</Typography></li>
              <li><Typography>Toivomasi ratkaisu</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              7.3 Käsittelyaika
            </Typography>
            <Typography paragraph>Pyrimme:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Kuittaamaan vastaanoton 2 arkipäivän kuluessa</Typography></li>
              <li><Typography>Tutkimaan ja vastaamaan 14 arkipäivän kuluessa</Typography></li>
              <li><Typography>Ratkaisemaan monimutkaiset tapaukset 30 arkipäivän kuluessa</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              7.4 Jatkotoimenpiteet
            </Typography>
            <Typography paragraph>Jos et ole tyytyväinen vastaukseemme, voit ottaa yhteyttä:</Typography>
            <Box component="ol" sx={{ ml: 4 }}>
              <li>
                <Typography>
                  <strong>Kuluttajariitalautakunta:</strong> Sovittelua varten
                </Typography>
              </li>
              <li>
                <Typography>
                  <strong>Kuluttajaneuvonta:</strong> Maksuton neuvontapalvelu
                </Typography>
              </li>
              <li>
                <Typography>
                  <strong>Euroopan verkkovälitteinen riidanratkaisualusta (ODR):</strong>{' '}
                  <Link href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener">
                    ec.europa.eu/consumers/odr
                  </Link>
                </Typography>
              </li>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Kuluttajariitalautakunnan yhteystiedot
              </Typography>
              <Typography variant="body2">Kuluttajariitalautakunta</Typography>
              <Typography variant="body2">PL 306, 00531 Helsinki</Typography>
              <Typography variant="body2">Verkkosivusto: kuluttajariita.fi</Typography>
              <Typography variant="body2">Puhelin: 029 566 5200</Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 8. Toimitusehdot */}
          <section id="delivery">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              8. Toimitusehdot
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              8.1 Toimitusajat
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Palvelutyyppi</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Arvioitu toimitusaika</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Seuranta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Pikatoimitus</TableCell>
                    <TableCell>1–2 arkipäivää</TableCell>
                    <TableCell>Saatavilla</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Standarditoimitus</TableCell>
                    <TableCell>2–5 arkipäivää</TableCell>
                    <TableCell>Saatavilla</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Ympäristöystävällinen toimitus</TableCell>
                    <TableCell>3–7 arkipäivää</TableCell>
                    <TableCell>Saatavilla</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              8.2 Toimitusyritykset
            </Typography>
            <Typography paragraph>Kotitoimituksissa:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Toimitamme kerran sovittuna aikana</Typography></li>
              <li><Typography>Jos vastaanottaja ei ole paikalla, jätetään ilmoitus</Typography></li>
              <li><Typography>Paketti säilytetään lähimmällä noutopisteellä 7 päivää</Typography></li>
              <li><Typography>7 päivän jälkeen paketti palautetaan lähettäjälle (palautuskulut veloitetaan)</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              8.3 Pakettivaatimukset
            </Typography>
            <Typography paragraph>Pakettien tulee täyttää seuraavat vaatimukset:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Paino:</strong> Enintään 35 kg (ympäristöystävälliset vaihtoehdot: 20 kg)</Typography></li>
              <li><Typography><strong>Mitat:</strong> Enintään 120 cm × 80 cm × 80 cm</Typography></li>
              <li><Typography><strong>Pakkaus:</strong> Asianmukaisesti suljettu ja merkitty</Typography></li>
              <li><Typography><strong>Kielletyt tuotteet:</strong> Ei vaarallisia, laittomia tai helposti pilaantuvia tavaroita</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              8.4 Vastaanottajan velvollisuudet
            </Typography>
            <Typography paragraph>Vastaanottajan tulee:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Olla paikalla toimitusosoitteessa toimitusikkunan aikana</Typography></li>
              <li><Typography>Tarkastaa paketti toimituksen yhteydessä</Typography></li>
              <li><Typography>Ilmoittaa näkyvistä vaurioista välittömästi</Typography></li>
              <li><Typography>Esittää henkilöllisyystodistus pyydettäessä</Typography></li>
            </Box>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 9. Vastuunrajoitus */}
          <section id="liability">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              9. Vastuunrajoitus
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              9.1 Vastuumme
            </Typography>
            <Typography paragraph>Olemme vastuussa:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Pakettien turvallisesta kuljetuksesta ehtojemme mukaisesti</Typography></li>
              <li><Typography>Toimituksesta arvioiduissa aikatauluissa (ylivoimaista estettä lukuun ottamatta)</Typography></li>
              <li><Typography>Asianmukaisesta käsittelystä valitun palvelutason mukaan</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              9.2 Vastuun ulkopuolelle jäävät tilanteet
            </Typography>
            <Typography paragraph>Emme vastaa:</Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Viivästyksistä, jotka johtuvat virheellisistä osoitetiedoista</Typography></li>
              <li><Typography>Vastaanottajan poissaolosta</Typography></li>
              <li><Typography>Sääolosuhteista tai ylivoimaisesta esteestä</Typography></li>
              <li><Typography>Tulliviivästyksistä kansainvälisissä toimituksissa</Typography></li>
              <li><Typography>Puutteellisesti ilmoitetuista tai pakatuista sisällöistä</Typography></li>
              <li><Typography>Välillisistä tai epäsuorista vahingoista</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              9.3 Ylivoimainen este
            </Typography>
            <Typography paragraph>
              Emme vastaa viivästyksistä tai laiminlyönneistä, jotka johtuvat meistä riippumattomista syistä, kuten luonnonkatastrofeista, lakoista, viranomaistoimenpiteistä, pandemioista tai infrastruktuurin häiriöistä.
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 10. Tietosuoja */}
          <section id="privacy">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              10. Tietosuoja
            </Typography>
            <Typography paragraph>
              Käsittelemme henkilötietoja GDPR:n ja Suomen tietosuojalakien mukaisesti. Lisätietoja:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Mitä tietoja keräämme</Typography></li>
              <li><Typography>Miten käytämme tietojasi</Typography></li>
              <li><Typography>Oikeutesi tietojesi suhteen</Typography></li>
              <li><Typography>Tietojen säilytysajat</Typography></li>
              <li><Typography>Tietojen jakaminen kolmansille osapuolille</Typography></li>
            </Box>
            <Typography paragraph>
              Katso tietosuojakäytäntömme (saatavilla verkkosivustollamme).
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 11. Muutokset ehtoihin */}
          <section id="changes">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              11. Muutokset ehtoihin
            </Typography>
            <Typography paragraph>
              Pidätämme oikeuden muuttaa näitä Ehtoja milloin tahansa. Muutoksista:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Ilmoitetaan verkkosivustollamme päivitetyllä "Päivitetty viimeksi" -päivämäärällä</Typography></li>
              <li><Typography>Tiedotetaan rekisteröityneille asiakkaille sähköpostitse</Typography></li>
              <li><Typography>Tulevat voimaan 30 päivää julkaisemisen jälkeen</Typography></li>
            </Box>
            <Typography paragraph>
              Palveluiden jatkuva käyttö muutosten voimaantulon jälkeen katsotaan muutettujen Ehtojen hyväksymiseksi.
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 12. Sovellettava laki */}
          <section id="law">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              12. Sovellettava laki ja tuomiovalta
            </Typography>
            <Typography paragraph>
              Näihin Ehtoihin sovelletaan Suomen lakia. Näistä Ehdoista tai palveluistamme mahdollisesti aiheutuvat riidat ratkaistaan Suomen tuomioistuimissa, ensimmäisenä oikeusasteena Helsingin käräjäoikeus.
            </Typography>
            <Typography paragraph>
              Kuluttajasopimuksissa saatetaan soveltaa asuinmaasi pakottavaa kuluttajansuojalainsäädäntöä, ja voit nostaa kanteen paikallisessa tuomioistuimessa.
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 13. Yhteystiedot */}
          <section id="contact">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              13. Yhteystiedot
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Asiakaspalvelu</Typography>
              <Typography variant="body2">Sähköposti: info@pakettihuolto.fi</Typography>
              <Typography variant="body2">Puhelin: +358 50 505 8167</Typography>
              <Typography variant="body2">Aukioloajat: Maanantai–perjantai, 9:00–17:00 (EET/EEST)</Typography>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Reklamaatiot</Typography>
              <Typography variant="body2">Sähköposti: info@pakettihuolto.fi</Typography>
              <Typography variant="body2">Puhelin: +358 50 505 8167</Typography>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Yrityskyselyt</Typography>
              <Typography variant="body2">Sähköposti: info@pakettihuolto.fi</Typography>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Postiosoite</Typography>
              <Typography variant="body2">Pakettihuolto Oy</Typography>
              <Typography variant="body2">Y-tunnus: 3219435-6</Typography>
              <Typography variant="body2">Suomi</Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* Kuittaus */}
          <Alert severity="success" sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Hyväksyntä
            </Typography>
            <Typography paragraph>
              Tekemällä tilauksen alustamme kautta (jonka operoi Pakettihuolto Oy) vahvistat, että olet lukenut, ymmärtänyt ja sitoudut noudattamaan näitä Myyntiehtoja.
            </Typography>
            <Typography variant="body2">
              <strong>Voimaantulopäivä:</strong> 16. tammikuuta 2026
            </Typography>
            <Typography variant="body2">
              <strong>Versio:</strong> 1.0
            </Typography>
          </Alert>
        </Paper>
      </Container>

      {/* Alatunniste */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4, mt: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" align="center" gutterBottom>
            &copy; 2026 Pakettihuolto Oy. Kaikki oikeudet pidätetään.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Link href="#payment" color="inherit" underline="hover">Maksaminen</Link>
            <Link href="#withdrawal" color="inherit" underline="hover">Peruuttamisoikeus</Link>
            <Link href="#returns" color="inherit" underline="hover">Palautukset</Link>
            <Link href="#complaints" color="inherit" underline="hover">Reklamaatiot</Link>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};